import React, { useEffect, useState } from "react";
import { useRoute } from "../contexts/RouterContext";
import type { Route, ViewName } from "../hooks/useRouter";

/* ── Inline SVG Icons (Lucide-style) ──────────────────────── */

const icons: Record<string, React.ReactNode> = {
  live: (
    <svg viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="3" />
      <path d="M16.24 7.76a6 6 0 010 8.49m-8.48-.01a6 6 0 010-8.49m11.31-2.82a10 10 0 010 14.14m-14.14 0a10 10 0 010-14.14" />
    </svg>
  ),
  sessions: (
    <svg viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  settings: (
    <svg viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  ),
};

interface NavItem {
  view: ViewName;
  label: string;
  route: Route;
}

const navItems: NavItem[] = [
  { view: "live", label: "Live", route: { view: "live" } },
  { view: "sessions", label: "Sessions", route: { view: "sessions", mode: "list" } },
  { view: "settings", label: "Settings", route: { view: "settings" } },
];

const COLLAPSE_KEY = "vtms.sidebar.collapsed";

export function Sidebar() {
  const { route, navigate } = useRoute();
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
    } catch {
      // kiosk private mode: fine to forget
    }
  }, [collapsed]);

  return (
    <nav className={`sidebar ${collapsed ? "sidebar-collapsed" : ""}`}>
      <div className="sidebar-nav">
        {navItems.map((item) => (
          <button
            key={item.view}
            className={`sidebar-nav-item ${route.view === item.view ? "active" : ""}`}
            onClick={() => navigate(item.route)}
            title={item.label}
            aria-current={route.view === item.view ? "page" : undefined}
            aria-label={collapsed ? item.label : undefined}
          >
            <span className="sidebar-nav-icon">{icons[item.view]}</span>
            {!collapsed && <span className="sidebar-nav-label">{item.label}</span>}
          </button>
        ))}
      </div>
      <button
        className="sidebar-collapse-btn"
        onClick={() => setCollapsed((c) => !c)}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        title={collapsed ? "Expand" : "Collapse"}
      >
        {collapsed ? "\u25B6" : "\u25C0"}
      </button>
    </nav>
  );
}
